import React, { KeyboardEvent } from "react"
import { useId } from "@reach/auto-id"
import { FormFields, FormRule, FormError, FormInputValue } from "models/form"
import { useTranslation } from "react-i18next"

import { handleInternalInputValidation } from "../formUtils"
import { isNotEmpty, isInt } from "utils/validation"
import { Errors } from "../Errors"

import "./InputCodeVerification.scoped.scss"

export type InputCodeVerificationProps = FormFields & {
	value?: string
	classNameInput?: string
	codeLength?: number
	hasResetCode?: boolean
}

const defaultCodeField = ""

export const InputCodeVerification: React.FC<InputCodeVerificationProps> = React.forwardRef(
	(
		{
			// Attributes
			id,
			value,
			name,
			label = "",
			disabled,
			readonly,
			codeLength = 6,
			// Styles
			className = "",
			classNameLabel = "",
			// Error management
			required = false,
			customRules,
			showErrors,
			// Handler
			onChange,
			onFocus,
			onBlur,
			onValidate,
		},
		ref: React.Ref<HTMLInputElement>
	) => {
		const INPUT_TYPE = "text"
		const prefix = useId()
		const inputId = id ?? `${prefix}-${name}`
		const errorId = `${inputId}-error`

		const [codeField, setCodeField] = React.useState<string>(defaultCodeField)
		const [formIsFocus, setFormIsFocus] = React.useState<boolean>(false)
		const [errors, setErrors] = React.useState<FormError[]>([])

		const { t } = useTranslation()

		const inputRef = React.useRef(null)
		// React.useImperativeHandle(
		// 	ref as React.Ref<Partial<HTMLInputElement>>,
		// 	() => ({
		// 		// if a parent component want be able to access inner ref with
		// 		// a forwadedRef and for instance call inputRef.current.focus()
		// 		// See https://reactjs.org/docs/hooks-reference.html#useimperativehandle
		// 		focus: () => inputRef.current.focus(),
		// 	})
		// )

		const values = codeField.split("")

		const selectedIndex = values.length < codeLength ? values.length : codeLength - 1

		const rules: FormRule[] = React.useMemo(
			() => [
				{
					key: "input-empty",
					check: (value: string) => (required ? isNotEmpty(value) : true),
					message: t("error_mandatory_field", { ns: "form", fieldName: label }),
				},
				{
					key: "code-length",
					check: (value: string) => value.length === codeLength,
					message: t("code_length", { ns: "form" }),
				},
				{
					key: "code-valid-number",
					check: (value: string) => isInt(value),
					message: t("error_invalid_value", { ns: "form" }),
				},
				...(customRules || []),
			],
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[required]
		)
		// customRules in dependencies array trigger an infinite loop,
		// because custom rules include a check property defining a new function,
		// preventing useMemo to return a valid shallow-comparison
		// or even useDeepMemo to return a valid deep-comparison

		const validate = React.useCallback(
			(currentValue: string) =>
				handleInternalInputValidation(currentValue, name, rules, setErrors, onValidate),
			[rules, name, onValidate]
		)

		// validate on mount and each time value change
		React.useEffect(() => {
			validate(value)
			return () => {
				// On unmounted errors and validation are no longer needed
				setErrors([])
				if (onValidate) onValidate(name, [])
			}
		}, [value, validate, onValidate, name])

		// Focus on the "first input" on load
		React.useEffect(() => {
			inputRef.current.focus()
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [])

		const handleClick = () => {
			if (inputRef.current) {
				inputRef.current.focus()
				// Place the focus at the end of the current value
				inputRef.current.selectionStart = inputRef.current.value.length
				inputRef.current.selectionEnd = inputRef.current.value.length
			}
		}

		const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
			const inputValue: FormInputValue<string> = {
				target: {
					name: name,
					value: event.target.value,
				},
			}
			setCodeField(event.target.value.slice(0, codeLength))
			onChange && onChange(inputValue as React.ChangeEvent<HTMLInputElement>)
		}

		const handleKeyUp = (event: KeyboardEvent) => {
			if (event.key === "Backspace") {
				setCodeField((prevCodeField) => prevCodeField.slice(0, prevCodeField.length + 1))
			}
		}

		// Retrieve css variables for the block c-code-verification.
		// Then, insert returned data on input, directly
		const codeVerificationEl = document.getElementsByClassName("c-input-code-verification")[0]

		const codeVerificationCSSVariable = codeVerificationEl
			? getComputedStyle(codeVerificationEl, null).getPropertyValue(
					"--inputCodeVerificationSize"
			  )
			: null

		// codeField can be reset by an external button
		React.useEffect(() => {
			const codeFieldIsReset = value === defaultCodeField && codeField !== value

			if (codeFieldIsReset) {
				setCodeField(defaultCodeField)
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [value])

		return (
			<div className="c-form-field m-auto">
				{label && (
					<label className={`c-label ${classNameLabel}`} htmlFor={inputId}>
						{label} {required && "*"}
					</label>
				)}
				<div
					className={`
					c-input-code-verification
					${disabled ? "is-disabled" : ""}
					${className}
				`}
				>
					<div className="flex">
						{Array.from({ length: codeLength }, (_, i) => {
							// Note that each character of the code has is own input-like display
							// but there is only a single hidden input
							// prettier-ignore

							const isLastActive = values.length === i
							const lastActiveClassname = isLastActive ? "is-last-active" : ""

							const isFilled = values.length > i
							const filledInputClassname = isFilled ? "is-filled" : ""

							const isLastItem = values.length === codeLength && i === codeLength - 1
							const isFocused = formIsFocus && (isLastActive || isLastItem)
							const activeFocusClassname = isFocused ? "is-focused" : ""

							return (
								<div className="c-input-code-verification__display" key={i}>
									<div
										className={`
										c-input-code-verification__number
										${lastActiveClassname}
										${filledInputClassname}
										${activeFocusClassname}
									`}
									>
										{values[i]}
									</div>
								</div>
							)
						})}
					</div>

					<input
						value={codeField}
						ref={inputRef}
						id={inputId}
						name={name}
						className="c-input-code-verification__input"
						disabled={disabled}
						required={required}
						aria-label={label}
						maxLength={codeLength}
						type={INPUT_TYPE}
						inputMode="tel"
						autoComplete="one-time-code"
						style={{
							left: `${selectedIndex * parseInt(codeVerificationCSSVariable)}px`,
						}}
						onClick={handleClick}
						onChange={(event) => {
							!readonly && onChange && handleChange(event)
						}}
						onKeyUp={(event) => {
							!readonly && handleKeyUp(event)
						}}
						onFocus={(event) => {
							setFormIsFocus(true)
							onFocus && onFocus(event)
						}}
						onBlur={(event) => {
							setFormIsFocus(false)
							onBlur && onBlur(event)
						}}
						onPaste={(event) => {
							return (
								// Prevent onPaste if it's not a number
								isNaN(parseInt(event.clipboardData.getData("Text"))) &&
								event.preventDefault()
							)
						}}
						aria-describedby={errorId}
						aria-invalid={Boolean(errors.length)}
					/>
				</div>

				{/* Error messages */}
				{showErrors && <Errors id={errorId} errors={errors} />}
			</div>
		)
	}
)

export default InputCodeVerification
